import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Field, Form, Formik } from 'formik';

import { flutterAdminApi } from './api';
import { buildFlutterBuilderValues } from './builder';
import type { FlutterBuilderValues } from './types';

export const Route = createFileRoute('/dashboard/feeds/build-old')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      eventIds: typeof search.eventIds === 'string' ? search.eventIds : '',
      leagueCode: typeof search.leagueCode === 'string' ? search.leagueCode : 'uk',
    };
  },
  component: FlutterBuildPage,
});

function FlutterBuildPage() {
  const navigate = useNavigate();
  const { eventIds, leagueCode } = Route.useSearch();
  const [marketSearch, setMarketSearch] = React.useState('');
  const [openEventIds, setOpenEventIds] = React.useState<string[]>([]);

  const providerEventIds = React.useMemo(
    () =>
      eventIds
        .split(',')
        .map(v => v.trim())
        .filter(Boolean),
    [eventIds]
  );

  const snapshotQ = useQuery({
    queryKey: ['flutter-admin-snapshot', providerEventIds],
    queryFn: () => flutterAdminApi.getCombinedSnapshot(providerEventIds),
    enabled: providerEventIds.length > 0,
  });

  if (!providerEventIds.length) {
    return <div>No events selected.</div>;
  }

  if (!leagueCode) {
    return <div>No league selected.</div>;
  }

  if (snapshotQ.isLoading) return <div>Loading…</div>;
  if (snapshotQ.isError) {
    return <div>Error: {(snapshotQ.error as Error).message}</div>;
  }

  const snapshot = snapshotQ.data;
  if (!snapshot) return <div>No snapshot found</div>;

  const initialValues = buildFlutterBuilderValues(snapshot);

  return (
    <Formik<FlutterBuilderValues>
      initialValues={initialValues}
      enableReinitialize={false}
      onSubmit={async (values, helpers) => {
        try {
          const selectedQuestions = values.questions
            .filter(question => question.enabled)
            .map(question => ({
              questionGroupKey: question.questionGroupKey,
              providerEventId: question.providerEventId,
              providerEventName: question.providerEventName,
              questionText: question.questionText.trim(),
              questionType: question.questionType,
              sourceMarkets: question.sourceMarkets,
              options: question.options
                .filter(option => option.enabled)
                .map(option => ({
                  logicalOptionKey: option.logicalOptionKey,
                  optionText: option.optionText.trim(),
                  offers: option.offers,
                })),
            }))
            .filter(question => question.questionText)
            .filter(question => question.options.length >= 2);

          if (!selectedQuestions.length) {
            helpers.setStatus({
              error: 'Select at least one valid grouped market with at least two options.',
            });
            return;
          }

          const result = await flutterAdminApi.createPickem({
            leagueCode,
            gameName: values.gameName.trim(),
            startUtc: values.startUtc,
            questions: selectedQuestions,
          });

          helpers.setStatus({ success: `Created pickem ${result.pickemId}` });

          await navigate({
            to: '/dashboard/pickems/$id',
            params: { id: result.pickemId },
            search: { league_id: undefined },
          });
        } catch (error) {
          helpers.setStatus({
            error: error instanceof Error ? error.message : 'Failed to create pickem',
          });
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting, status }) => {
        const groupedQuestions = groupQuestionsByEvent(values.questions);
        const selectedQuestions = values.questions.filter(q => q.enabled);
        const normalizedSearch = marketSearch.trim().toLowerCase();

        return (
          <Form className="space-y-6 p-12">
            <div className="space-y-4 rounded-xl border p-4">
              <div>
                <label className="block text-sm font-medium">Game name</label>
                <Field name="gameName" className="mt-1 w-full rounded-md border px-3 py-2" />
              </div>

              <div>
                <label className="block text-sm font-medium">Start UTC</label>
                <Field name="startUtc" className="mt-1 w-full rounded-md border px-3 py-2" />
              </div>

              {status?.error ? (
                <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {status.error}
                </div>
              ) : null}

              {status?.success ? (
                <div className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-700">
                  {status.success}
                </div>
              ) : null}
            </div>

            <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
              <section className="space-y-4">
                <div className="rounded-xl border p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold">Available markets</h2>
                      <p className="text-sm text-muted-foreground">
                        Add the grouped markets you want to turn into questions.
                      </p>
                    </div>

                    <div className="text-sm text-muted-foreground">
                      {selectedQuestions.length} selected
                    </div>
                  </div>

                  <div className="mt-4">
                    <input
                      value={marketSearch}
                      onChange={e => setMarketSearch(e.target.value)}
                      placeholder="Search grouped markets or events"
                      className="w-full rounded-md border px-3 py-2"
                    />
                  </div>
                </div>

                {groupedQuestions.map(group => {
                  const visibleQuestions = group.questions.filter(question => {
                    if (!normalizedSearch) return true;

                    const sourceMarketNames = question.sourceMarkets
                      .map(market => market.providerMarketName)
                      .join(' ')
                      .toLowerCase();

                    const brands = question.brands.join(' ').toLowerCase();

                    return (
                      question.questionText.toLowerCase().includes(normalizedSearch) ||
                      question.providerEventName.toLowerCase().includes(normalizedSearch) ||
                      sourceMarketNames.includes(normalizedSearch) ||
                      brands.includes(normalizedSearch)
                    );
                  });

                  if (!visibleQuestions.length) {
                    return null;
                  }

                  const isOpen = openEventIds.includes(group.providerEventId);
                  const selectedCount = group.questions.filter(q => q.enabled).length;

                  return (
                    <div key={group.providerEventId} className="rounded-xl border">
                      <button
                        type="button"
                        onClick={() => {
                          setOpenEventIds(current =>
                            current.includes(group.providerEventId)
                              ? current.filter(id => id !== group.providerEventId)
                              : [...current, group.providerEventId]
                          );
                        }}
                        className="flex w-full items-center justify-between p-4 text-left"
                      >
                        <div>
                          <div className="font-semibold">{group.providerEventName}</div>
                          <div className="text-sm text-muted-foreground">
                            {visibleQuestions.length} candidate grouped markets • {selectedCount}{' '}
                            selected
                          </div>
                        </div>

                        <div className="text-sm">{isOpen ? 'Hide' : 'Show'}</div>
                      </button>

                      {isOpen && (
                        <div className="space-y-3 border-t p-4">
                          {visibleQuestions.map(question => {
                            const questionIndex = values.questions.findIndex(
                              q => q.questionGroupKey === question.questionGroupKey
                            );

                            const previewOptions = question.options
                              .slice(0, 3)
                              .map(o => o.optionText)
                              .join(' / ');

                            return (
                              <div
                                key={question.questionGroupKey}
                                className="rounded-lg border p-3"
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="min-w-0">
                                    <div className="font-medium">
                                      {question.questionText} ({question.brands.join(', ')})
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      {question.options.length} logical options
                                      {previewOptions ? ` • ${previewOptions}` : ''}
                                    </div>
                                    <div className="mt-1 text-xs text-muted-foreground">
                                      Source markets:{' '}
                                      {question.sourceMarkets
                                        .map(
                                          market => `${market.brand}: ${market.providerMarketName}`
                                        )
                                        .join(' • ')}
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      setFieldValue(
                                        `questions.${questionIndex}.enabled`,
                                        !values.questions[questionIndex].enabled
                                      )
                                    }
                                    className="rounded-md border px-3 py-1.5 text-sm"
                                  >
                                    {values.questions[questionIndex].enabled ? 'Remove' : 'Add'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>

              <aside className="space-y-4">
                <div className="sticky top-4 space-y-4">
                  <div className="rounded-xl border p-4">
                    <h2 className="text-lg font-semibold">Selected questions</h2>
                    <p className="text-sm text-muted-foreground">
                      Edit only the grouped markets you have added.
                    </p>
                  </div>

                  {selectedQuestions.length === 0 ? (
                    <div className="rounded-xl border p-4 text-sm text-muted-foreground">
                      No grouped markets selected yet.
                    </div>
                  ) : (
                    selectedQuestions.map(question => {
                      const questionIndex = values.questions.findIndex(
                        q => q.questionGroupKey === question.questionGroupKey
                      );

                      return (
                        <div key={question.questionGroupKey} className="rounded-xl border p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-xs text-muted-foreground">
                                {question.providerEventName}
                              </div>
                              <div className="font-semibold">{question.questionText}</div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                Brands: {question.brands.join(', ')}
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                Source markets:{' '}
                                {question.sourceMarkets
                                  .map(market => `${market.brand}: ${market.providerMarketName}`)
                                  .join(' • ')}
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                setFieldValue(`questions.${questionIndex}.enabled`, false)
                              }
                              className="rounded-md border px-2 py-1 text-sm"
                            >
                              Remove
                            </button>
                          </div>

                          <div className="mt-3">
                            <label className="block text-sm font-medium">Question text</label>
                            <Field
                              name={`questions.${questionIndex}.questionText`}
                              className="mt-1 w-full rounded-md border px-3 py-2"
                            />
                          </div>

                          <div className="mt-3">
                            <label className="block text-sm font-medium">Question type</label>
                            <Field
                              name={`questions.${questionIndex}.questionType`}
                              as="select"
                              className="mt-1 w-full rounded-md border px-3 py-2"
                            >
                              <option value="team">Team</option>
                              <option value="player">Player</option>
                              <option value="prop">Prop</option>
                              <option value="tiebreaker">Tie Breaker</option>
                            </Field>
                          </div>

                          <div className="mt-4 space-y-2">
                            {question.options.map((option, oi) => (
                              <div key={option.logicalOptionKey} className="rounded-lg border p-3">
                                <label className="flex items-center gap-3">
                                  <Field
                                    type="checkbox"
                                    name={`questions.${questionIndex}.options.${oi}.enabled`}
                                  />
                                  <span>{option.optionText}</span>
                                </label>

                                <div className="mt-2">
                                  <label className="block text-sm font-medium">Option text</label>
                                  <Field
                                    name={`questions.${questionIndex}.options.${oi}.optionText`}
                                    className="mt-1 w-full rounded-md border px-3 py-2"
                                  />
                                </div>

                                <div className="mt-2 text-sm text-muted-foreground">
                                  {option.offers.length} brand offer
                                  {option.offers.length === 1 ? '' : 's'}
                                </div>

                                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                                  {option.offers.map(offer => (
                                    <div
                                      key={`${offer.brand}-${offer.providerSelectionId}`}
                                      className="rounded border px-2 py-1"
                                    >
                                      {offer.brand} • Selection ID: {offer.providerSelectionId}
                                      {offer.oddsDecimal != null
                                        ? ` • Odds ${offer.oddsDecimal}`
                                        : ''}
                                      <div className="mt-0.5">
                                        Market: {offer.providerMarketName}
                                      </div>
                                      {offer.selectionName !== option.optionText ? (
                                        <div className="mt-0.5">
                                          Feed label: {offer.selectionName}
                                        </div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="rounded-md border px-4 py-2 disabled:opacity-50"
                    >
                      {isSubmitting ? 'Creating…' : 'Create pickem'}
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

function groupQuestionsByEvent(questions: FlutterBuilderValues['questions']) {
  const groups = new Map<
    string,
    {
      providerEventId: string;
      providerEventName: string;
      questions: FlutterBuilderValues['questions'];
    }
  >();

  for (const question of questions) {
    const existing = groups.get(question.providerEventId);

    if (existing) {
      existing.questions.push(question);
      continue;
    }

    groups.set(question.providerEventId, {
      providerEventId: question.providerEventId,
      providerEventName: question.providerEventName,
      questions: [question],
    });
  }

  return Array.from(groups.values());
}
